/**
 * StatusPageToggle — extra section for CheckEditDrawer that allows toggling
 * whether a check is displayed on the tenant's public status page.
 *
 * Designed as an ExtraSectionRenderer for the shared UI's CheckEditDrawer,
 * so standalone checker (which has no status pages) never sees this.
 */
import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import type { CheckDefinition } from "@/lib/api";

interface StatusPageService {
  id: string;
  tenant_id: string;
  check_uuid: string;
  display_name: string;
  sort_order: number;
  created_at: string;
}

interface ServicesResponse {
  services: StatusPageService[];
  quota: { current: number; limit: number };
}

interface CheckDefFromAPI {
  uuid: string;
  name: string;
  [key: string]: unknown;
}

/**
 * Returns an ExtraSectionRenderer function that can be passed to
 * CheckEditDrawer via the Management component's checkEditExtraSections prop.
 *
 * The check object passed by ExtraSectionRenderer may have an empty name
 * (e.g. when opened from the extra section before full check data loads).
 * The inner component fetches the check definition name from the API as
 * a fallback to ensure display names are never empty.
 */
export function createStatusPageSection() {
  return function StatusPageSection(
    check: Partial<CheckDefinition>,
    _onChange: (check: Partial<CheckDefinition>) => void
  ) {
    return <StatusPageToggleInner checkUuid={check.uuid} checkName={check.name} />;
  };
}

function StatusPageToggleInner({
  checkUuid,
  checkName,
}: {
  checkUuid?: string;
  checkName?: string;
}) {
  const [loading, setLoading] = useState(true);
  const [serviceEntry, setServiceEntry] = useState<StatusPageService | null>(null);
  const [quota, setQuota] = useState<{ current: number; limit: number } | null>(null);
  const [toggling, setToggling] = useState(false);
  const [error, setError] = useState("");
  const [resolvedName, setResolvedName] = useState(checkName || "");

  // Only show for existing checks (not during creation)
  const isNewCheck = !checkUuid;

  const loadServices = useCallback(async () => {
    if (isNewCheck) return;
    setLoading(true);
    try {
      const data = await api.get<ServicesResponse>("/status-page/services");
      setQuota(data.quota);
      const match = data.services.find((s) => s.check_uuid === checkUuid);
      setServiceEntry(match ?? null);
    } catch {
      // Status page feature not available (e.g. free plan) — hide quietly
      setQuota({ current: 0, limit: 0 });
    } finally {
      setLoading(false);
    }
  }, [checkUuid, isNewCheck]);

  // When checkName is empty, fetch the actual check definition name
  useEffect(() => {
    if (checkName) {
      setResolvedName(checkName);
      return;
    }
    if (!checkUuid) return;
    // Fetch check definitions to find this check's name
    api
      .get<CheckDefFromAPI[]>("/check-definitions")
      .then((defs) => {
        const found = defs.find((d) => d.uuid === checkUuid);
        if (found?.name) setResolvedName(found.name);
      })
      .catch(() => {
        // Ignore — will fall back to UUID
      });
  }, [checkUuid, checkName]);

  useEffect(() => {
    loadServices();
  }, [loadServices]);

  // Don't render for new checks or if quota is 0 (feature not available)
  if (isNewCheck) return null;
  if (!loading && quota && quota.limit === 0) return null;

  const isOnStatusPage = !!serviceEntry;
  const atQuotaLimit = quota && quota.limit !== -1 && quota.current >= quota.limit;

  const handleToggle = async () => {
    if (!checkUuid) return;
    setToggling(true);
    setError("");
    try {
      if (isOnStatusPage && serviceEntry) {
        // Remove from status page
        await api.del(`/status-page/services/${serviceEntry.id}`);
        setServiceEntry(null);
        if (quota) setQuota({ ...quota, current: quota.current - 1 });
      } else {
        // Add to status page — use resolved name, never empty/UUID
        const name = resolvedName || checkName || checkUuid;
        const created = await api.post<StatusPageService>("/status-page/services", {
          check_uuid: checkUuid,
          display_name: name,
        });
        setServiceEntry(created);
        if (quota) setQuota({ ...quota, current: quota.current + 1 });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update status page");
    } finally {
      setToggling(false);
    }
  };

  return (
    <section className="space-y-3">
      <h3 className="text-sm font-semibold border-b border-border pb-2 pt-1">
        Status Page
      </h3>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading...</p>
      ) : (
        <div className="space-y-2">
          <label className="flex items-center gap-3 cursor-pointer">
            <button
              type="button"
              role="switch"
              aria-checked={isOnStatusPage}
              disabled={toggling || (!isOnStatusPage && !!atQuotaLimit)}
              onClick={handleToggle}
              className={`relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 disabled:cursor-not-allowed ${
                isOnStatusPage ? "bg-primary" : "bg-input"
              }`}
            >
              <span
                className={`pointer-events-none block h-4 w-4 rounded-full bg-background shadow-lg ring-0 transition-transform ${
                  isOnStatusPage ? "translate-x-4" : "translate-x-0"
                }`}
              />
            </button>
            <span className="text-sm font-medium">
              {toggling ? "Updating..." : "Display on status page"}
            </span>
          </label>

          {isOnStatusPage && serviceEntry && (
            <p className="text-xs text-muted-foreground pl-12">
              Shown as: <span className="font-medium text-foreground">{serviceEntry.display_name}</span>
            </p>
          )}

          {!isOnStatusPage && !atQuotaLimit && (
            <p className="text-xs text-muted-foreground pl-12">
              Not currently shown on the public status page.
            </p>
          )}

          {!isOnStatusPage && atQuotaLimit && quota && (
            <p className="text-xs text-amber-600 dark:text-amber-400 pl-12">
              Service limit reached ({quota.current}/{quota.limit}). Remove a service or upgrade your plan.
            </p>
          )}

          {error && (
            <p className="text-xs text-red-600 dark:text-red-400 pl-12">{error}</p>
          )}
        </div>
      )}
    </section>
  );
}
